import { Space } from "antd";
import { EntityTypes } from "@vzbl/integrations";
import { Time } from "src/components/Time";
import { GcalIcon } from "src/components/GcalIcon";
import { useApplicationContext } from "src/hooks/useApplication";
import { ActionCard } from "./components/ActionCard";
import { useEntityContext } from "../useEntityContext";
import { VzblTag } from "../components/VzblTag";
import { TabbedWork, TabbedWorkItems } from "../components/TabbedWork";
import { generateWorkTabs } from "../components/worktabs";
import { History, HistoryComponent } from "../History";
import { Insights } from "../Insights";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { MetricTag, SummaryTag } from "./components/Tags";
import { SentencesList } from "./components/SentencesList";
import { ProgressMetric } from "./components/ProgressMetric";
import { HeaderAttribute } from "./components/HeaderAttributes";
import { EntityTabs } from "./components/EntityTabs";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";

export const CalendarEntryEntity = () => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entity = context.dataRef.current.entity.properties;
  const entityType = context.dataRef.current.entity.labels[0];

  const {
    attendeesNumber,
    description,
    startTime,
    summary,
    vzblQualityScore,
    vzblValueStatement,
  } = entity;

  const headerAttributes: HeaderAttribute[] = [
    {
      value: "start",
      child: (
        <Time
          relative={false}
          format={"MMM D, YYYY h:mm A"}
          value={startTime}
        />
      ),
    },
    {
      value: "duration in hours",
      child: (
        <VzblTag
          asLink
          orgId={orgId}
          context={context}
          text={entity.totalCostInHours}
          color="none"
          linkParams={{
            orgId,
            entityType: entityType,
            attribute: "totalCostInHours",
            value: entity.totalCostInHours,
          }}
        />
      ),
    },
    {
      value: "attendees",
      child: (
        <VzblTag
          asLink
          orgId={orgId}
          context={context}
          text={attendeesNumber}
          color="none"
          linkParams={{
            orgId,
            entityType: entityType,
            attribute: "attendeesNumber",
            value: attendeesNumber,
          }}
        />
      ),
    },
  ];

  const workTabsItems: TabbedWorkItems[] = generateWorkTabs(
    orgId,
    entity.uuid,
    [{ entityTypes: EntityTypes.Issue }]
  );

  const tabs = [
    {
      key: "overview",
      id: "overview",
      label: "Overview",
      children: <Insights entity={context.dataRef.current.entity} />,
    },
    {
      key: "2",
      id: "2",
      label: "Work",
      children: (
        <Space
          direction="vertical"
          size={10}
          style={{ width: "100%", paddingTop: 10 }}
        >
          <TabbedWork title="Work" items={workTabsItems} />
        </Space>
      ),
    },
    {
      key: "5",
      id: "5",
      label: "History",
      children: (
        <History>
          <HistoryComponent />
        </History>
      ),
    },
  ];

  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel
          entity={context.dataRef.current.entity}
          attributes={headerAttributes}
          icon={<GcalIcon size={40} />}
          label={summary}
        />
      }
      summaryPanel={<SummaryPanel title={summary} description={description} />}
      insightsPanel={
        <InsightsPanel
          cards={[
            vzblValueStatement && (
              <ActionCard
                key="value-statement"
                badge={<SummaryTag />}
                label="Value Statement"
                value={<SentencesList data={vzblValueStatement} />}
              />
            ),
            vzblQualityScore !== undefined && (
              <ActionCard
                key="quality-score"
                badge={<MetricTag />}
                label="Quality Score"
                value={
                  <ProgressMetric
                    higherBetter
                    value={vzblQualityScore}
                    data={""}
                  />
                }
              />
            ),
          ].filter(Boolean)}
        />
      }
    >
      <EntityTabs
        propertiesSkipList={[
          "attendeesNumber",
          "description",
          "startTime",
          "summary",
          "name",
          "vzblQualityScore",
          "vzblValueStatement",
        ]}
        entity={context.dataRef.current.entity}
        tabs={tabs}
      />
    </EntityScreen>
  );
};
